// formsAPI.js
// Centralized API calls for form operations

import config from '../config/config';

/**
 * Fetches all forms available to the user
 * @param {string} token - Authentication token
 * @returns {Promise<{status: boolean, data?: Array, error?: string}>}
 */
export const getAllForms = async (token) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}${config.endpoints.forms.get}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.status) {
      return { status: true, data: result.data };
    } else {
      return { status: false, error: 'Failed to fetch forms' };
    }
  } catch (err) {
    return {
      status: false,
      error: `Error loading forms: ${err.message}`
    };
  }
};

/**
 * Fetches a specific form by ID
 * @param {string|number} id - Form ID
 * @param {string} token - Authentication token
 * @returns {Promise<{status: boolean, data?: Object, error?: string}>}
 */
export const getFormById = async (id, token) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}${config.endpoints.forms.get}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.status) {
      return { status: true, data: result.data };
    } else {
      return { status: false, error: 'Failed to fetch form' };
    }
  } catch (err) {
    return {
      status: false,
      error: `Error loading form: ${err.message}`
    };
  }
};

/**
 * Saves a form (create or update)
 * @param {Object} formData - Form data to save
 * @param {string} formData.title - Form title
 * @param {string} formData.description - Form description
 * @param {string} formData.slug - URL slug
 * @param {Array} formData.json_definition - Form field definitions
 * @param {string|number|null} formData.id - Form ID (null for new forms)
 * @param {string|number} formData.createdby - User ID (optional, for new forms)
 * @param {string} token - Authentication token
 * @returns {Promise<{status: boolean, id?: number, error?: string}>}
 */
export const saveForm = async (formData, token) => {
  try {
    const response = await fetch(`${config.API_BASE_URL}${config.endpoints.forms.save}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.status) {
      return {
        status: true,
        id: result.id,
        message: 'Form saved successfully'
      };
    } else {
      return { status: false, error: 'Failed to save form' };
    }
  } catch (err) {
    return {
      status: false,
      error: `Error saving form: ${err.message}`
    };
  }
};
